import { useI18n } from "vue-i18n";
import { toUid, type Uid } from "@/helpers/utility/Id";
import { ensureInt, notNull } from "@/helpers/utility/Type";

import { commonOrDefault } from "@/helpers/utility/CollectionFunctions";
import { abbreviateTitle } from "@/helpers/utility/Text";
import { formatRoundName } from "../simulationRound/useRoundTime";
import { applyOrder } from "@/helpers/utility/Array";

import { collectEffectConsequences } from "@editor/items/simulationRound/simulationRoundSearch";
import { getRoundActivatedActions, getRoundActivatedIncidents, getRoundActivatedScheduledEffects, useReactiveChartData } from "./useChartData";
import { VIRTUAL_END_ROUND_INDEX } from "@editor/items/charts/ChartItem";

import type { SimulationItem } from "@editor/items/simulation/SimulationItem";
import type { SimulationRoundItem } from "@editor/items/simulationRound/SimulationRoundItem";
import type {
  DataEntry,
  EntryValue,
  ColumnEntry,
  LegendEntry,
  ValueType
} from "@editor/items/charts/ChartData";
import type {
  SelectableChartItem,
  SelectableChartItemType,
  ItemConfigMap
} from "@editor/items/charts/ChartItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { RelationItem } from "../relation/RelationItem";

type ChartDataContext = ReturnType<typeof useReactiveChartData>;

type ChartDataMapperContext = {
  // Chart data:
  chartUid?: string;
  chartType: string;
  itemConfigs: ItemConfigMap;
  dataToggles: ChartDataContext["dataToggles"];

  selectedItem: ChartDataContext["selectedItem"];
  selectedItems: ChartDataContext["selectedItems"];
  selectedSimulation: ChartDataContext["selectedSimulation"];
  selectedSimulations: ChartDataContext["selectedSimulations"];

  availableSettingsValues: ChartDataContext["availableSettingsValues"];
  chartAxisConfig: ChartDataContext["chartAxisConfig"];
  commonRoundSettings: ChartDataContext["commonRoundSettings"];

  simulationRounds: ChartDataContext["simulationRounds"];
  playedRoundsInRange: ChartDataContext["playedRoundsInRange"];
  roundsInRange: ChartDataContext["roundsInRange"];

  orderedTargets: ChartDataContext["orderedTargets"];
  orderedResources: ChartDataContext["orderedResources"];
  orderedIndices: ChartDataContext["orderedIndices"];
  availableItems: ChartDataContext["availableItems"];
  allAvailableTargets: ChartDataContext["allAvailableTargets"];
  allAvailableResources: ChartDataContext["allAvailableResources"];
  allAvailableIndices: ChartDataContext["allAvailableIndices"];
  allTargetsOrder: ChartDataContext["allTargetsOrder"];
  allResourcesOrder: ChartDataContext["allResourcesOrder"];
  allIndicesOrder: ChartDataContext["allIndicesOrder"];

  actionActivations: ChartDataContext["actionActivations"];
  incidentActivations: ChartDataContext["incidentActivations"];
  scheduledEffectActivations: ChartDataContext["scheduledEffectActivations"];

  elementColors: Record<string, string | null | undefined>;
  elementUnits: Record<string, string | null | undefined>;
  outgoingRelationsByElement: Record<Uid<"target" | "resource" | "index">, RelationItem[]>;

  titleAbbreviations: Record<Uid<string>, string>;

  activeSimulationId: number | null;
};
type AvailableItem = ChartDataContext["availableItems"][SelectableChartItemType][number];

/**
 * Collect data according to the current chart settings
 * and return it as a structured array.
 */
export function mapChartData(
  axes: {
    x: string; // "simulation" | "target" | "round" | "resource" | "simulationRound";
    y: string; // "value" | "gain" | "impulse";
  },
  context: ChartDataMapperContext,
  t: ReturnType<typeof useI18n>["t"]
): {
  data: DataEntry[];
  columns?: Map<string, ColumnEntry>;
  legend: { [groupName: string]: LegendEntry[] };
} {
  const uid = context.chartUid || "none";
  const chartType = context.chartType || "bar";

  const settings = context.availableSettingsValues;
  const chartAxisConfig = context.chartAxisConfig;

  const selectedSimulations = context.selectedSimulations;
  const selectedItem = context.selectedItem;
  const selectedItems = context.selectedItems;
  const selectedSimulation = context.selectedSimulation;

  const actionActivations = context.actionActivations;
  const incidentActivations = context.incidentActivations;
  const scheduledEffectActivations = context.scheduledEffectActivations;

  const simulationRounds = context.simulationRounds;
  const playedRoundsInRange = context.playedRoundsInRange;
  const roundsInRange = context.roundsInRange;
  const orderedTargets = context.orderedTargets;
  const orderedResources = context.orderedResources;
  const orderedIndices = context.orderedIndices;
  const availableItems = context.availableItems;
  const allAvailableIndices = context.allAvailableIndices;
  const allAvailableTargets = context.allAvailableTargets;
  const allAvailableResources = context.allAvailableResources;
  const allTargetsOrder = context.allTargetsOrder;
  const allResourcesOrder = context.allResourcesOrder;
  const allIndicesOrder = context.allIndicesOrder;
  const elementUnits = context.elementUnits;
  const outgoingRelationsByElement = context.outgoingRelationsByElement;

  const selectedElement = selectedItem ? availableItems[selectedItem.type][ensureInt(selectedItem.id)] : null;

  const singleSimulationOnly = chartAxisConfig?.singleSimulation;

  const singleRoundOnly = chartAxisConfig?.roundMode == "pair";

  const columns = new Map<string, ColumnEntry>();
  const legend: { [groupName: string]: LegendEntry[] } = {};
  const data: DataEntry[] = [];

  const {
    getColor,
    getItemTitle,
    getItemColor,
    getRoundColor,
    getSimulationColor,
    getSimulationSymbol,
    getSimulationId,
    getRoundColumnKey,
    buildSimulationRoundColumns
  } = useGetterFunctions(context, t);

  // ======================
  // ==== COLLECT DATA ====

  // TARGETS
  if (axes.x == "target") {
    // TARGET - VALUE
    if (axes.y == "value") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];

        for (const round of rounds) {
          const values = orderedTargets.map((target): EntryValue => {
            let value: number | null = null;
            const valueGroup = round.elementValues[target.uid];
            if (valueGroup) {
              value = Number(round.elementValues[target.uid].value);
            }
            let startValue: number | null = value;
            if (round.previousRound?.elementValues?.[target.uid]) {
              startValue = Number(round.previousRound.elementValues[target.uid].value);
            }

            const columnKey = `${simulation.uid}.${target.uid}`;

            if (!(columnKey in columns)) {
              const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
              columns.set(columnKey, {
                key: columnKey,
                type: target.type,
                id: target.id,
                shortTitle: getItemTitle(target, true),
                title: getItemTitle(target, false),
                groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
                groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
                withSimulation: selectedSimulations.length > 1,
                values: []
              });
            }

            const simulationId = getSimulationId(simulation.id);

            return {
              key: target.uid,
              uid: target.uid,
              id: target.id,
              columnId: columnKey,
              shortTitle: getItemTitle(target, true),
              title: getItemTitle(target, false),
              type: target.type,
              startValue,
              value,
              color: getItemColor("target", target.id, simulationId),
              scale: 1,
              symbol: simulationSymbol || "circle",
              unit: target.unit,
              roundIndex: round.index,
              simulationId: simulationId
            };
          });

          const simulationId = getSimulationId(simulation.id);
          const roundColor = getRoundColor(round.index, simulationId);

          if (values.length > 0) {
            const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
            const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);
            data.push({
              key: simulationKey + roundKey,
              id: simulationId + "-" + round.index,
              shortTitle: getItemTitle(simulation, true),
              title: getItemTitle(simulation, false),
              type: simulation.type,
              values: values,
              symbol: simulationSymbol,
              color: roundColor || getSimulationColor(simulation.id)
            });
          }
        }
      }
    }

    // TARGET - GAIN
    else if (axes.y == "gain") {
      for (const simulation of selectedSimulations) {
        const simulationId = getSimulationId(simulation.id);
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];

        for (const round of rounds) {
          const values = orderedTargets.map((target): EntryValue => {
            const startValue = 0;
            const impulseSubValues = getImpulseSubvalues(
              target,
              simulationId,
              simulationRounds[simulation.id] || [],
              round,
              startValue
            );

            const columnKey = `${simulation.uid}.${target.uid}`;

            // create column entry
            if (!(columnKey in columns)) {
              const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
              columns.set(columnKey, {
                key: columnKey,
                type: target.type,
                id: target.id,
                shortTitle: getItemTitle(target, true),
                title: getItemTitle(target, false),
                groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
                groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
                withSimulation: selectedSimulations.length > 1
              });
            }

            const value = Number(round.elementValues[target.uid].value);
            const previousValue = Number(round.previousRound?.elementValues?.[target.uid]?.value ?? value);

            return {
              key: target.uid,
              id: target.id,
              uid: target.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(target, true),
              title: getItemTitle(target, false),
              type: target.type,
              startValue: null,
              value: value - previousValue,
              color: getItemColor("target", target.id, simulationId),
              symbol: simulationSymbol || "circle",
              roundIndex: round.index,
              simulationId: simulationId,
              unit: target.unit,

              subValues: impulseSubValues
            };
          });

          if (values.length > 0) {
            const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
            const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);
            data.push({
              key: simulationKey + roundKey,
              id: simulationId + "-" + round.index,
              shortTitle: getItemTitle(simulation, true),
              title: getItemTitle(simulation, false),
              type: simulation.type,
              values: values,
              symbol: simulationSymbol,
              color: getColor("simulation", simulation.id)
            });
          }
        }
      }
    }

    // TARGET - IMPULSE
    else if (axes.y == "impulse") {
      for (const simulation of selectedSimulations) {
        const simulationId = getSimulationId(simulation.id);
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];

        for (const round of rounds) {
          const values = orderedTargets.map((target): EntryValue => {
            const startValue = 0;
            const impulseSubValues = getImpulseSubvalues(
              target,
              simulationId,
              context.simulationRounds[simulation.id] || [],
              round,
              startValue
            );

            const columnKey = `${simulation.uid}.${target.uid}`;

            // create column entry
            if (!(columnKey in columns)) {
              const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
              columns.set(columnKey, {
                key: columnKey,
                type: target.type,
                id: target.id,
                shortTitle: getItemTitle(target, true),
                title: getItemTitle(target, false),
                groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
                groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
                withSimulation: selectedSimulations.length > 1
              });
            }

            const value = Number(round.elementValues[target.uid].value);
            const previousValue = Number(
              round.previousRound?.elementValues?.[target.uid]?.value || 0
            );

            return {
              key: `simulation_${simulationId}.${round.index}.${target.uid}`,
              id: target.id,
              uid: target.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(target, true),
              title: getItemTitle(target, false),
              type: target.type,
              startValue: null,
              value: value - previousValue,
              color: getItemColor("target", target.id),
              symbol: simulationSymbol || "circle",
              unit: target.unit,
              numberFormat: "pointsFlexible",

              roundIndex: round.index,
              simulationId: simulationId,

              subValues: impulseSubValues
            };
          });

          if (values.length > 0) {
            const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
            const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);
            data.push({
              key: simulationKey + roundKey,
              id: simulationId + "-" + round.index,
              shortTitle: getItemTitle(simulation, true),
              title: getItemTitle(simulation, false),
              type: simulation.type,
              values: values,
              symbol: simulationSymbol,
              color: getColor("simulation", simulation.id)
            });
          }
        }
      }
    }

    // TARGET - EFFECT
    else if (axes.y == "effect") {
      const simulation = context.selectedSimulation;
      if (simulation == null) return { data: [], legend: {} };
      if (selectedItems.length == 0) return { data: [], legend: {} };

      const values: EntryValue[] = [];

      const allTargets = context.allAvailableTargets;

      // order the targets:
      const allTargetsOrdered = applyOrder(
        allTargets,
        allTargetsOrder
      );
      const allTargetIds = allTargetsOrdered.map((e) => e.id);

      for (const targetId of allTargetIds) {
        const target = allTargets[targetId];
        const columnKey = `${simulation.uid}.${target.uid}`;
        if (!columns.has(columnKey)) {
          columns.set(columnKey, {
            key: columnKey,
            shortTitle: getItemTitle(target, true),
            title: getItemTitle(target, false),
            id: target.id,
            type: target.type
          });
        }
      }

      const simulationId = getSimulationId(simulation.id);
      const roundsInRange = playedRoundsInRange[simulation.id];
      const roundsInRangeIds = new Set(roundsInRange.map((r) => r.index));
      const allSimulationRounds = simulation.rounds.filter((r) => r.played);

      for (const item of selectedItems) {
        if (
          isItemType(item, "actionActivation") ||
          isItemType(item, "incidentActivation") ||
          isItemType(item, "scheduledEffectActivation")
        ) {
          const activation =
            item.type == "actionActivation"
              ? actionActivations[item.id]
              : item.type == "incidentActivation"
              ? incidentActivations[item.id]
              : scheduledEffectActivations[item.id];

          // Go through the affected items and sum up the impulses
          // that were created as a consequence of the action activation
          const consequences = collectEffectConsequences(
            allSimulationRounds,
            roundsInRangeIds,
            {
              id: activation.itemId,
              type: activation.itemType,
              roundIndex: activation.roundIndex
            },
            outgoingRelationsByElement
          );
          
          const firstRoundImpulses = Object.values(
            consequences.consequencesByRound
          )[0];

          for (const targetId of allTargetIds) {
            const target = allTargets[targetId];

            const firstRoundImpulse = firstRoundImpulses.find(
              (impulse) =>
                impulse.affectedType == target.type &&
                impulse.affectedId == target.id
            );

            const targetValue = values.find((v) => v.id == target.id);

            const value = consequences.target?.[target.id] ?? 0;

            if (targetValue) {
              targetValue.startValue =
                (targetValue.startValue || 0) +
                (firstRoundImpulse?.pointsToAdd ?? 0);
              targetValue.value = (targetValue.value || 0) + value;
            } else {
              const columnKey = `${simulation.uid}.${target.uid}`;
              values.push({
                key: target.uid,
                id: target.id,
                uid: target.uid,
                columnId: columnKey,
                type: "target",
                shortTitle: getItemTitle(target, true),
                title: getItemTitle(target, false),
                startValue: firstRoundImpulse?.pointsToAdd ?? 0,
                value: value,
                symbol: "circle",
                numberFormat: "pointsFlexible",
                unit: target.unit,

                roundIndex: activation.roundIndex,
                simulationId: simulationId
              });
            }
          }
        }
      }

      if (values.length > 0) {
        const dataEntry = {
          key: "action-focus",
          id: "action-focus",
          type: "action",
          shortTitle: "actions",
          title: "actions",
          values
        };

        data.push(dataEntry);
      }
    }
  }

  // RESOURCES
  else if (axes.x == "resource") {
    // RESOURCE - VALUE
    if (axes.y == "value") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];
        const simulationId = getSimulationId(simulation.id);

        for (const round of rounds) {
          const values = orderedResources.map((resource): EntryValue => {
            const columnKey = `${simulation.uid}.${resource.uid}`;
            const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
            columns.set(columnKey, {
              key: columnKey,
              type: resource.type,
              id: resource.id,
              title: getItemTitle(resource, false),
              shortTitle: getItemTitle(resource, true),
              groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
              groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
              withSimulation: selectedSimulations.length > 1
            });

            const startValue = Number(
              round.previousRound?.elementValues?.[resource.uid]?.value ?? 0
            );
            const value = Number(round.elementValues[resource.uid]?.value ?? 0);

            return {
              key: resource.uid,
              id: resource.id,
              uid: resource.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(resource, true),
              title: getItemTitle(resource, false),
              type: resource.type,
              startValue,
              value,
              color: getItemColor("resource", resource.id),
              symbol: simulationSymbol || "circle",
              numberFormat: "value",
              unit: resource.unit,

              roundIndex: round.index,
              simulationId: simulationId
            };
          });

          if (values.length == 0) continue;

          const roundColor = getRoundColor(round.index, simulation.id);

          const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
          const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);

          data.push({
            key: simulationKey + roundKey,
            id: simulationId + "-" + round.index,
            shortTitle: getItemTitle(simulation, true),
            title: getItemTitle(simulation, false),
            type: simulation.type,
            values: values,
            symbol: simulationSymbol,
            color: roundColor || getSimulationColor(simulation.id)
          });
        }
      }
    }

    // RESOURCE - GAIN
    else if (axes.y == "gain") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];
        const simulationId = getSimulationId(simulation.id);

        for (const round of rounds) {
          const values = orderedResources.map((resource): EntryValue => {
            const startValue = 0;
            const impulseSubValues = getImpulseSubvalues(
              resource,
              simulationId,
              simulationRounds[simulation.id] || [],
              round,
              startValue
            );

            const columnKey = `${simulation.uid}.${resource.uid}`;
            
            // Update resource column
            const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
            columns.set(columnKey, {
              key: columnKey,
              type: resource.type,
              id: resource.id,
              title: getItemTitle(resource, false),
              shortTitle: getItemTitle(resource, true),
              groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
              groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
              withSimulation: selectedSimulations.length > 1
            });

            return {
              key: resource.uid,
              id: resource.id,
              uid: resource.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(resource, true),
              title: getItemTitle(resource, false),
              type: resource.type,
              startValue: null,
              value:
                Number(round.elementValues[resource.uid].value) -
                Number(round.previousRound?.elementValues?.[resource.uid].value ?? 0),
              symbol: simulationSymbol || "circle",
              roundIndex: round.index,
              simulationId: simulationId,
              numberFormat: "pointsFlexible",
              unit: resource.unit,

              subValues: impulseSubValues
            };
          });

          if (values.length == 0) continue;

          const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
          const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);

          data.push({
            key: simulationKey + roundKey,
            id: simulationId + "-" + round.index,
            shortTitle: getItemTitle(simulation, true),
            title: getItemTitle(simulation, false),
            type: simulation.type,
            values: values,
            symbol: simulationSymbol
          });
        }
      }
    }

    // RESOURCE - IMPULSE
    else if (axes.y == "impulse") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];
        const simulationId = getSimulationId(simulation.id);

        for (const round of rounds) {
          const values = orderedResources.map((resource): EntryValue => {
            const startValue = 0;
            const impulseSubValues = getImpulseSubvalues(
              resource,
              simulationId,
              simulationRounds[simulation.id] || [],
              round,
              startValue
            );

            const columnKey = `${simulation.uid}.${resource.uid}`;

            // Update resource column
            const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
            columns.set(columnKey, {
              key: columnKey,
              type: resource.type,
              id: resource.id,
              title: getItemTitle(resource, false),
              shortTitle: getItemTitle(resource, true),
              groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
              groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
              withSimulation: selectedSimulations.length > 1
            });

            return {
              key: resource.uid,
              id: resource.id,
              uid: resource.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(resource, true),
              title: getItemTitle(resource, false),
              type: resource.type,
              startValue: null,
              value:
                Number(round.elementValues[resource.uid].value) -
                Number(round.previousRound?.elementValues?.[resource.uid].value || 0),
              symbol: simulationSymbol || "circle",
              roundIndex: round.index,
              simulationId: simulationId,
              numberFormat: "pointsFlexible",
              unit: resource.unit,

              subValues: impulseSubValues
            };
          });

          if (values.length == 0) continue;

          const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
          const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);

          data.push({
            key: simulationKey + roundKey,
            id: simulationId + "-" + round.index,
            shortTitle: getItemTitle(simulation, true),
            title: getItemTitle(simulation, false),
            type: simulation.type,
            values: values,
            symbol: simulationSymbol
          });
        }
      }
    }

    // RESOURCE - EFFECT
    else if (axes.y == "effect") {
      const simulation = selectedSimulation;
      if (simulation == null) return { data: [], legend: {} };

      const allSimulationRounds = simulation.rounds.filter((r) => r.played);
      const roundsInRange = playedRoundsInRange[simulation.id];
      const simulationId = getSimulationId(simulation.id);

      const allResources = allAvailableResources;

      // order the resources:
      const allResourcesOrdered = applyOrder(
        allResources,
        allResourcesOrder
      );
      const allResourceIds = allResourcesOrdered.map((e) => e.id);

      for (const resourceId of allResourceIds) {
        const resource = allResources[resourceId];
        if (!columns.has(resource.uid)) {
          columns.set(resource.uid, {
            key: resource.uid,
            shortTitle: getItemTitle(resource, true),
            title: getItemTitle(resource, false),
            id: resource.id,
            type: resource.type
          });
        }
      }

      const values: EntryValue[] = [];

      for (const item of selectedItems) {
        if (
          isItemType(item, "actionActivation") ||
          isItemType(item, "incidentActivation") ||
          isItemType(item, "scheduledEffectActivation")
        ) {
          const activation =
            item.type == "actionActivation"
              ? actionActivations[item.id]
              : item.type == "incidentActivation"
              ? incidentActivations[item.id]
              : scheduledEffectActivations[item.id];

          if (activation == null) {
            console.warn("activation not found", item);
            continue;
          }

          // Go through the affected items and sum up the impulses
          // that were created as a consequence of the action activation
          const consequences = collectEffectConsequences(
            allSimulationRounds,
            new Set(roundsInRange.map((r) => r.index)),
            {
              id: activation.itemId,
              type: activation.itemType,
              roundIndex: activation.roundIndex
            },
            outgoingRelationsByElement
          );

          const firstRoundImpulses = Object.values(
            consequences.consequencesByRound
          )[0];

          for (const resourceId of allResourceIds) {
            const resource = allResources[resourceId];

            const firstRoundImpulse = firstRoundImpulses.find(
              (impulse) =>
                impulse.affectedType == resource.type &&
                impulse.affectedId == resource.id
            );

            const valueEntry = values.find((v) => v.id == resource.id);

            const startValue = firstRoundImpulse?.pointsToAdd ?? 0;
            const value = consequences.resource?.[resource.id] ?? 0;

            if (valueEntry) {
              valueEntry.startValue = (valueEntry.startValue || 0) + startValue;
              valueEntry.value = (valueEntry.value || 0) + value;
            } else {
              values.push({
                key: resource.uid,
                id: resource.id,
                uid: resource.uid,
                columnId: resource.uid,
                type: resource.type,
                shortTitle: getItemTitle(resource, true),
                title: getItemTitle(resource, false),
                startValue,
                value,
                symbol: "circle",
                numberFormat: "pointsFlexible",
                roundIndex: activation.roundIndex,
                simulationId: simulationId,
                unit: resource.unit
              });
            }
          }
        }
      }

      if (values.length > 0) {
        const dataEntry = {
          key: `resource-effects`,
          id: "resource-effects",
          type: "resource-effects",
          shortTitle: "resource-effects",
          title: "resource-effects",
          values
        };

        data.push(dataEntry);
      }
    }
  }

  // INDICES
  else if (axes.x == "index") {
    // INDEX - VALUE
    if (axes.y == "value") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];
        const simulationId = getSimulationId(simulation.id);

        for (const round of rounds) {
          const values = orderedIndices.map((index): EntryValue => {
            const value = Number(round.elementValues[index.uid]?.value);
            const unit = index.unit || round.elementValues[index.uid]?.unit;
            const startValue = Number(round.previousRound?.elementValues?.[index.uid]?.value ?? value);

            const columnKey = `${simulation.uid}.${index.uid}`;

            if (!(columnKey in columns)) {
              const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
              columns.set(columnKey, {
                key: columnKey,
                type: index.type,
                id: index.id,
                title: getItemTitle(index, false),
                shortTitle: getItemTitle(index, true),
                groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
                groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
                withSimulation: selectedSimulations.length > 1,
                values: []
              });
            }

            return {
              key: `${simulation.uid}.${index.uid}`,
              id: index.id,
              uid: index.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(index, true),
              title: getItemTitle(index, false),
              type: index.type,
              startValue,
              value,
              color: getItemColor("index", index.id, simulation.id),
              scale: 1,
              symbol: simulationSymbol || "circle",
              unit,
              roundIndex: round.index,
              simulationId: simulationId
            };
          });

          const roundColor = getRoundColor(round.index, simulationId);

          if (values.length > 0) {
            const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
            const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);
            data.push({
              key: simulationKey + roundKey,
              id: simulationId + "-" + round.index,
              shortTitle: getItemTitle(simulation, true),
              title: getItemTitle(simulation, false),
              type: simulation.type,
              values: values,
              symbol: simulationSymbol,
              color: roundColor || getSimulationColor(simulation.id)
            });
          }
        }
      }
    }

    // INDEX - GAIN
    else if (axes.y == "gain") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const rounds = roundsInRange[simulation.id];
        const simulationId = getSimulationId(simulation.id);

        for (const round of rounds) {
          const values = orderedIndices.map((index): EntryValue => {
            const startValue = 0;
            const impulseSubValues = getImpulseSubvalues(
              index,
              simulationId,
              simulationRounds[simulation.id] || [],
              round,
              startValue
            );

            const columnKey = `${simulation.uid}.${index.uid}`;

            // create column entry
            if (!(columnKey in columns)) {
              const columnSimulation = selectedSimulations.length > 1 ? simulation : null;
              columns.set(columnKey, {
                key: columnKey,
                type: index.type,
                id: index.id,
                title: getItemTitle(index, false),
                shortTitle: getItemTitle(index, true),
                groupTitle: columnSimulation ? getItemTitle(columnSimulation, false) : undefined,
                groupShortTitle: columnSimulation ? getItemTitle(columnSimulation, true) : undefined,
                withSimulation: selectedSimulations.length > 1
              });
            }

            const value = Number(round.elementValues[index.uid]?.value);
            const previousValue = Number(
              round.previousRound?.elementValues?.[index.uid]?.value || 0
            );
            const unit = index.unit || round.elementValues[index.uid]?.unit;

            return {
              key: `${simulation.uid}.${index.uid}`,
              id: index.id,
              uid: index.uid,
              columnId: columnKey,
              shortTitle: getItemTitle(index, true),
              title: getItemTitle(index, false),
              type: index.type,
              startValue: null,
              value: value - previousValue,
              color: getItemColor("index", index.id),
              symbol: simulationSymbol || "circle",
              roundIndex: round.index,
              simulationId: simulationId,
              unit,
              numberFormat: "pointsFlexible",

              subValues: impulseSubValues
            };
          });

          if (values.length > 0) {
            const simulationKey = singleSimulationOnly ? "single-simulation" : simulation.uid;
            const roundKey = (singleRoundOnly ? "" : `.simulationRound_i${round.index}`);
            data.push({
              key: simulationKey + roundKey,
              id: simulationId + "-" + round.index,
              shortTitle: getItemTitle(simulation, true),
              title: getItemTitle(simulation, false),
              type: simulation.type,
              values: values,
              symbol: simulationSymbol,
              color: getColor("simulation", simulation.id)
            });
          }
        }
      }
    }
  }

  // ROUNDS
  else if (axes.x == "simulationRound") {
    // ROUND - VALUE
    if (axes.y == "value") {
      for (const simulation of selectedSimulations) {
        const simulationSymbol = getSimulationSymbol(simulation.id);
        const simulationId = getSimulationId(simulation.id);
        const rounds = roundsInRange[simulation.id];

        // build data
        for (const selectedItem of selectedItems) {
          // Get the color of the item:
          const color = getItemColor(
            selectedItem.type,
            selectedItem.id,
            simulation.id
          );

          // skip invisible entries in multi-entry charts:
          if (!chartAxisConfig?.singleItem && !isVisibleColor(color)) continue;

          const values = rounds
            .map((round): EntryValue | null => {
              const previousRound = round.previousRound ?? round;

              let value: number | null = round.totalValue ?? 0;
              let lastValue: number | null = previousRound.totalValue ?? 0;
              let unit = "%";

              const uid = selectedItem.type + "_" + selectedItem.id;

              if (isItemType(selectedItem, "resource") || isItemType(selectedItem, "index") || isItemType(selectedItem, "target")) {
                const valueGroup = round.elementValues[uid];
                if (!round.played) {
                  value = null;
                  lastValue = null;
                  unit = availableItems[selectedItem.type][selectedItem.id]?.unit ?? "";
                }
                else if (!valueGroup) {
                  value = null;
                  lastValue = null;

                  const startValueGroup = simulation.startElementValues[uid];
                  if (startValueGroup) {
                    unit = simulation.startElementValues[uid].unit ?? "";
                  } else if (isItemType(selectedItem, "index")) {
                    unit = elementUnits[uid] ?? "";
                  } else {
                    unit = availableItems[selectedItem.type][selectedItem.id]?.unit ?? "";
                  }
                } else {
                  value = Number(valueGroup?.value ?? 0);
                  lastValue = Number(previousRound.elementValues[uid]?.value ?? value);
                  unit = valueGroup?.unit || "";
                }
              }

              return {
                key: simulationId + "-" + round.index,
                id: round.index,
                uid: `round_${round.index}`,
                columnId: getRoundColumnKey(simulation.id, round.index),
                shortTitle: round.index + 1 + "",
                title: round.index + 1 + "",
                type: "simulationRound",
                startValue: lastValue,
                value: value,
                symbol: simulationSymbol || "circle",
                numberFormat: "value" as const,
                unit: unit,
                color: color,

                roundIndex: round.index,
                simulationId: simulationId
              };
            })
            .filter(notNull);

          if (values.length > 0) {
            data.push({
              key: `simulation_${simulationId}.${selectedItem.type}_${selectedItem.id}`,
              id: selectedItem.id,
              type: selectedItem.type,
              shortTitle: getItemTitle(selectedItem, true),
              title: getItemTitle(selectedItem, false),
              groupTitle: selectedSimulations.length > 1 ? getItemTitle(simulation, false) : undefined,
              groupShortTitle: selectedSimulations.length > 1 ? getItemTitle(simulation, true) : undefined,
              values: values,
              symbol: simulationSymbol,
              color: color
            });
          }
        }

        // build columns
        buildSimulationRoundColumns(columns, simulation, rounds);
      }
    }

    // ROUND - GAIN
    else if (axes.y == "gain") {
      if (!selectedItem) {
        return { data: [], legend: {} };
      }

      for (const simulation of selectedSimulations) {
        const simulationId = getSimulationId(simulation.id);
        const simulationSymbol = getSimulationSymbol(simulation.id);

        const rounds = roundsInRange[simulation.id];

        const values = rounds.map((round): EntryValue => {
          const previousRound = round.previousRound ?? round;

          let value: number | null = round.totalValue ?? 0;
          let lastValue: number | null = previousRound.totalValue ?? 0;
          let unit = "%";

          const uid = selectedItem.type + "_" + selectedItem.id;

          // total
          if (isItemType(selectedItem, "resource") || isItemType(selectedItem, "index") || isItemType(selectedItem, "target")) {
            const valueGroup = round.elementValues[uid];
            if (!round.played) {
              value = null;
              lastValue = null;
              unit = availableItems[selectedItem.type][selectedItem.id]?.unit ?? "";
            } else if (!valueGroup) {
              value = null;
              lastValue = null;
              unit = availableItems[selectedItem.type][selectedItem.id]?.unit || "";
            } else {
              value = Number(valueGroup.value);
              lastValue = Number(previousRound.elementValues[uid]?.value ?? value);
              unit = valueGroup.unit;
            }
          }

          const subValues = getImpulseSubvalues(
            selectedElement,
            simulationId,
            simulationRounds[simulation.id] || [],
            round,
            0
          );

          return {
            key: simulationId + "-" + round.index,
            id: round.index,
            uid: `round_${round.index}`,
            columnId: getRoundColumnKey(simulation.id, round.index),
            shortTitle: round.index + 1 + "",
            title: round.index + 1 + "",
            type: "simulationRound",
            startValue: null,
            value: value !== null && lastValue !== null ? value - lastValue : null,
            symbol: simulationSymbol || "circle",
            roundIndex: round.index,
            simulationId: simulationId,
            numberFormat: "pointsFlexible" as const,
            unit,

            subValues
          };
        });

        data.push({
          key: `simulation_${simulationId}.${selectedItem.type}_${selectedItem.id}`,
          id: selectedItem.id,
          type: selectedItem.type,
          shortTitle: getItemTitle(selectedItem, true),
          title: getItemTitle(selectedItem, false),
          values: values
        });

        // build columns
        buildSimulationRoundColumns(columns, simulation, rounds);
      }
    }

    // ROUND - IMPULSE
    else if (axes.y == "impulse") {
      if (!selectedItem) {
        return { data: [], legend: {} };
      }

      for (const simulation of selectedSimulations) {
        const simulationId = getSimulationId(simulation.id);
        const simulationSymbol = getSimulationSymbol(simulation.id);

        const rounds = roundsInRange[simulation.id];

        const values = rounds.map((round): EntryValue => {
          const previousRound = round.previousRound ?? round;

          let value: number | null = round.totalValue ?? 0;
          let lastValue: number | null = previousRound.totalValue ?? 0;
          let unit = "%";

          const uid = selectedItem.type + "_" + selectedItem.id;

          if (isItemType(selectedItem, "resource") || isItemType(selectedItem, "target")) {
            const valueGroup = round.elementValues[uid];
            if (!valueGroup) {
              value = null;
              lastValue = null;
              unit = availableItems[selectedItem.type][selectedItem.id]?.unit || "";
            } else {
              value = round.played ? Number(valueGroup.value) : null;
              lastValue = Number(previousRound.elementValues[uid].value ?? value);
              unit = valueGroup.unit;
            }
          }

          const roundItem = { ...round };
          delete roundItem.previousRound;

          const elementId = ensureInt(selectedItem.id);
          const selectedElement = availableItems[selectedItem.type][elementId];

          const subValues = getImpulseSubvalues(
            selectedElement,
            simulationId,
            simulationRounds[simulation.id] || [],
            roundItem,
            0
          );

          return {
            key: simulationId + "-" + round.index,
            id: round.index,
            uid: `round_${round.index}`,
            columnId: getRoundColumnKey(simulation.id, round.index),
            shortTitle: round.index + 1 + "",
            title: round.index + 1 + "",
            type: "simulationRound",
            startValue: null,
            value: value !== null && lastValue !== null ? value - lastValue : null,
            symbol: simulationSymbol || "circle",
            numberFormat: "pointsFlexible",
            roundIndex: round.index,
            simulationId: simulationId,
            unit,
            subValues
          };
        });

        data.push({
          key: `simulation_${simulationId}.${selectedItem.type}_${selectedItem.id}`,
          id: selectedItem.id,
          type: selectedItem.type,
          shortTitle: getItemTitle(selectedItem, true),
          title: getItemTitle(selectedItem, false),
          values: values
        });

        // build columns
        buildSimulationRoundColumns(columns, simulation, rounds);
      }
    }

    // ROUND - IMPULSE SUM
    else if (axes.y == "impulseSum") {
      for (const simulation of selectedSimulations) {
        const simulationId = getSimulationId(simulation.id);
        const rounds = playedRoundsInRange[simulation.id];

        const simulationSymbol = getSimulationSymbol(simulation.id);

        for (const selectedItem of selectedItems) {
          if (
            !isItemType(selectedItem, "target") &&
            !isItemType(selectedItem, "resource")
          ) {
            continue;
          }

          const groups =
            (chartAxisConfig?.subgroups as ["increase", "decrease"]) ?? [];

          groups.forEach((subgroup) => {
            let lastValue = 0;

            // Get the color of the item:
            const color = getItemColor(
              selectedItem.type,
              selectedItem.id,
              simulation.id,
              subgroup
            );

            const values = rounds.map((round): EntryValue => {
              // const previousRound = round.previousRound ?? round;

              const impulse =
                round.impulses[selectedItem.type][selectedItem.id];

              const sums = (impulse?.sourceValues || []).reduce(
                (totals, sourceValue) => {
                  if (sourceValue.value == null) return totals;
                  if (sourceValue.value > 0) {
                    totals.increase += sourceValue.value;
                  } else {
                    totals.decrease -= sourceValue.value;
                  }
                  return totals;
                },
                { increase: 0, decrease: 0 }
              );

              let value: number = sums[subgroup] + lastValue;
              let unit = "%";

              if (isItemType(selectedItem, "resource")) {
                const resource = allAvailableResources[selectedItem.id];
                unit = resource.unit;
              }
              // target
              else if (isItemType(selectedItem, "target")) {
                const target = allAvailableTargets[selectedItem.id];
                unit = target.unit;
              }

              const result: EntryValue = {
                key: simulationId + "-" + round.index,
                id: round.index,
                uid: `round_${round.index}`,
                columnId: getRoundColumnKey(simulation.id, round.index),
                shortTitle: round.index + 1 + "",
                title: round.index + 1 + "",
                type: "simulationRound",
                startValue: lastValue,
                value: value,
                symbol: "circle",
                numberFormat: "value",
                roundIndex: round.index,
                simulationId: simulationId,
                color: color,
                unit
              };

              lastValue = value;
              return result;
            });

            data.push({
              key: `simulation_${simulationId}.${selectedItem.type}_${selectedItem.id}-${subgroup}`,
              id: selectedItem.id,
              type: selectedItem.type,
              shortTitle:
                getItemTitle(selectedItem, true) +
                ": " +
                t(`chartSubgroups.${subgroup}`),
              title:
                getItemTitle(selectedItem, false) +
                ": " +
                t(`chartSubgroups.${subgroup}`),
              color: color,
              values: values,
              symbol: simulationSymbol
            });
          });
        }

        buildSimulationRoundColumns(columns, simulation, rounds);
      }
    }
  }

  // CREATE LEGENDS

  if (chartType == "bar") {
    legend["chart"] = [];

    if (settings.endPointsVisible) {
      let title = t("labels.endValue");
      if (axes.y == "impulse" || axes.y == "gain") {
        title = t("labels.summedGain");
      }
      legend["chart"].push({
        title,
        type: "chart",
        id: 2,
        pattern: "url(#legend-circle)",
        patternWidth: 15,
        patternHeight: 17
      });
    }

    if (axes.y == "impulse") {
      // collect all impulse types used
      const usedTypes = new Set<string>();
      for (const d of data) {
        for (const v of d.values) {
          if (!v.subValues) continue;
          for (const s of v.subValues) {
            usedTypes.add(s.type);
          }
        }
      }

      if (usedTypes.has("scheduledEffect")) {
        legend["chart"].push({
          title: t("objectNames.scheduledEffect"),
          type: "chart",
          id: 0,
          color: `var(--effect-type-scheduledEffect)`
        });
      }
      if (usedTypes.has("action")) {
        legend["chart"].push({
          title: t("objectNames.action"),
          type: "chart",
          id: 2,
          color: `var(--effect-type-action)`
        });
      }
      if (usedTypes.has("delayed_action")) {
        legend["chart"].push({
          title: t("objectNames.delayed_action"),
          type: "chart",
          id: 4,
          color: `var(--effect-type-delayed_action)`
        });
      }
      if (usedTypes.has("incident")) {
        legend["chart"].push({
          title: t("objectNames.incident"),
          type: "chart",
          id: 6,
          color: `var(--effect-type-incident)`
        });
      }
      if (usedTypes.has("delayed_incident")) {
        legend["chart"].push({
          title: t("objectNames.delayed_incident"),
          type: "chart",
          id: 8,
          color: `var(--effect-type-delayed_incident)`
        });
      }
      if (usedTypes.has("relation")) {
        legend["chart"].push({
          title: t("objectNames.impact"),
          type: "chart",
          id: 10,
          color: `var(--effect-type-relation)`
        });
      }
      if (usedTypes.has("momentum")) {
        legend["chart"].push({
          title: t("objectNames.momentum"),
          type: "chart",
          id: 12,
          color: `var(--effect-type-momentum)`
        });
      }
    } else {
      legend["chart"].push(
        {
          title:
            axes.y == "effect"
              ? t("labels.directEffect")
              : t("labels.startValue"),
          type: "chart",
          id: 0,
          color: `rgb(54, 90, 120)`
        },
        {
          title:
            axes.y == "effect" ? t("labels.indirectGain") : t("labels.gain"),
          type: "chart",
          id: 1,
          pattern: `url("#${uid}-legend-bar-chart__pattern-ascending")`
        },
        {
          title:
            axes.y == "effect" ? t("labels.indirectLoss") : t("labels.loss"),
          type: "chart",
          id: -1,
          pattern: `url("#${uid}-legend-bar-chart__pattern-descending")`
        }
      );
    }
  } else if (chartType == "radar") {
    legend["chart"] = [];

    legend["chart"].push(
      {
        title: axes.y == "effect" ? t("labels.indirectGain") : t("labels.gain"),
        type: "chart",
        id: 1,
        color: "var(--color-text)",
        pattern: `url("#${uid}-legend-radar-chart__pattern-ascending")`,
        patternHeight: 14
      },
      {
        title: axes.y == "effect" ? t("labels.indirectLoss") : t("labels.loss"),
        type: "chart",
        id: -1,
        color: "var(--color-text)",
        pattern: `url("#${uid}-legend-radar-chart__pattern-descending")`,
        patternHeight: 14
      }
    );
  }

  if (selectedSimulations.length > 1) {
    legend["simulation"] = selectedSimulations.map((sim) => {
      const simulationSymbol = getSimulationSymbol(sim.id);
      return {
        title: getItemTitle(sim, false),
        type: "simulation",
        id: sim.id,
        symbol: simulationSymbol,
        color: getSimulationColor(sim.id)
      };
    });
  }

  if (chartAxisConfig?.hasRoundColors) {
    const simulations = selectedSimulations;
    legend["round"] = [];
    for (const simulation of simulations) {
      const rounds = playedRoundsInRange[simulation.id];

      for (const round of rounds) {
        // Skip if already in legend
        if (legend["round"].some((e) => e.id == round.index)) continue;

        let title = getItemTitle(
          { id: round.index, type: "simulationRound" },
          false
        );

        const colors = simulations
          .filter((sim) =>
            playedRoundsInRange[sim.id].some((r) => r.index == round.index)
          )
          .map((sim) => ({
            title: getItemTitle(sim, false),
            color: getRoundColor(round.index, sim.id),
            symbol: getSimulationSymbol(sim.id) || "circle"
          }));

        let color = commonOrDefault(colors, (e) => e.color, undefined);

        if (colors.every((e) => e.color == null)) {
          color = getRoundColor(round.index) || "var(--color-text)";
        }

        if (colors.length == 1 && simulations.length > 1) {
          title = colors[0].title + ": " + title;
        }

        legend["round"].push({
          type: "simulationRound",
          id: round.index,
          title,
          color,
          colors
        });
      }
    }
  }

  if (
    selectedItems.length > 0 &&
    (chartAxisConfig?.hasItemColors || settings.groupLabelsShortened)
  ) {
    legend["item"] = selectedItems.map((item): LegendEntry => {
      let simulationColors = selectedSimulations.flatMap(
        (
          sim
        ): {
          title: string;
          color: string | undefined;
          symbol: UnfilledSymbolName | null;
          subgroup?: string;
        }[] => {
          const simulationSymbol = getSimulationSymbol(sim.id);

          // if using subgroups
          // create an entry for each subgroup
          if (chartAxisConfig?.subgroups) {
            const groups = chartAxisConfig.subgroups;

            return groups.map((subgroup) => {
              const color = getItemColor(item.type, item.id, sim.id, subgroup);

              return {
                title:
                  t(`chartSubgroups.${subgroup}`) +
                  " – " +
                  getItemTitle(sim, false),
                color,
                symbol: simulationSymbol || "circle",
                subgroup
              };
            });
          }

          return [
            {
              title: getItemTitle(sim, false),
              color: getItemColor(item.type, item.id, sim.id),
              symbol: simulationSymbol || "circle",
              subgroup: undefined
            }
          ];
        }
      );

      // simplify simulations with subgroups
      if (chartAxisConfig?.subgroups) {
        const groups = chartAxisConfig.subgroups;

        // If all simulations of the same subgroup have the same color,
        // replace the simulation entries with a single entry for the subgroup
        for (const group of groups) {
          const groupColors = simulationColors
            .filter((e) => e.subgroup == group)
            .map((e) => e.color);

          if (groupColors.every((e) => e == groupColors[0])) {
            const color = groupColors[0];

            // remove subgroup entries
            simulationColors = simulationColors.filter(
              (e) => e.subgroup != group
            );

            simulationColors.push({
              title: t(`chartSubgroups.${group}`),
              color,
              symbol: null,
              subgroup: group
            });
          }
        }
      }

      let color = commonOrDefault(simulationColors, (e) => e.color, undefined);

      if (simulationColors.every((e) => e.color == null)) {
        color = "var(--color-text)";
      }

      const entry: LegendEntry = {
        type: item.type,
        id: item.id,
        title: getItemTitle(item, false),
        shortTitle: getItemTitle(item, true),
        isItem: true,
        color,
        colors: simulationColors
      };
      return entry;
    });
  }

  // Extra abbreviation data
  if (settings.xAxisLabelsShortened) {
    const legendItems = legend["item"] || [];
    if (!legend["item"]) legend["item"] = legendItems;

    let itemsToAdd: TargetItem[] | ResourceItem[] | IndexItem[] = [];

    if (axes.x == "target") {
      itemsToAdd = Object.values(allAvailableTargets).sort((a, b) => {
        return allTargetsOrder.indexOf(a.id) - allTargetsOrder.indexOf(b.id);
      });
    } else if (axes.x == "resource") {
      itemsToAdd = Object.values(allAvailableResources).sort((a, b) => {
        return (
          allResourcesOrder.indexOf(a.id) - allResourcesOrder.indexOf(b.id)
        );
      });
    } else if (axes.x == "index") {
      itemsToAdd = Object.values(allAvailableIndices).sort((a, b) => {
        return allIndicesOrder.indexOf(a.id) - allIndicesOrder.indexOf(b.id);
      });
    }

    for (const item of itemsToAdd) {
      const legendEntry = legendItems.find(
        (e) => e.id == item.id && e.type == item.type
      );

      if (legendEntry) {
        legendEntry.isColumn = true;
      } else {
        const newEntry: LegendEntry = {
          title: getItemTitle(item, false),
          shortTitle: getItemTitle(item, true),
          type: item.type,
          id: item.id,
          isColumn: true
        };
        legendItems.push(newEntry);
      }
    }
  }

  return { data, columns, legend };
}

function useGetterFunctions(ctx: ChartDataMapperContext, t: Function) {
  const itemConfigs: ItemConfigMap = ctx.itemConfigs || {
    resource: {},
    target: {},
    index: {},
    simulation: {},
    simulationRound: {},
    actionActivation: {},
    incidentActivation: {},
    scheduledEffectActivation: {}
  };
  const chartType = ctx.chartType || "bar";

  const chartAxisConfig = ctx.chartAxisConfig;
  const selectedSimulations = ctx.selectedSimulations;

  function getColor(
    type: SelectableChartItemType,
    id: number | string,
    simulationId?: number,
    subgroup?: string
  ) {
    let color = undefined;

    if (subgroup != null) {
      color = itemConfigs?.[type]?.[id]?.subgroups?.[subgroup]?.color ?? color;
    } else {
      color = itemConfigs?.[type]?.[id]?.color;
    }

    if (
      simulationId != null &&
      !chartAxisConfig?.singleSimulation &&
      selectedSimulations.length > 1
    ) {
      if (subgroup) {
        color =
          itemConfigs?.[type]?.[id]?.subgroups?.[subgroup]?.[simulationId]
            ?.color || color;
      } else {
        color = itemConfigs?.[type]?.[id]?.[simulationId]?.color ?? color;
      }
    }

    if (!color) {
      // No color? Return the fill color of the item.
      const uid = toUid(type, id);
      color = ctx.elementColors[uid];
    }

    return color;
  }
  function getItemColor(
    type: SelectableChartItemType,
    id: number | string,
    simulationId?: number,
    subgroup?: string,
    defaultColor: string = "var(--color-text)"
  ) {
    if (!chartAxisConfig?.hasItemColors) return undefined;
    return getColor(type, id, simulationId, subgroup) || defaultColor;
  }
  function getRoundColor(id: number | string, simulationId?: number) {
    if (!chartAxisConfig?.hasRoundColors) return undefined;
    if (ctx.dataToggles?.all?.simulationRound)
      return getColor("simulationRound", "last", simulationId);
    return getColor("simulationRound", id, simulationId);
  }
  function getSimulationColor(id: number | string) {
    if (!chartAxisConfig?.hasSimulationColors) return undefined;
    return getColor("simulation", id);
  }
  function getSimulationSymbol(id: number | string) {
    if (!chartAxisConfig?.hasSimulationSymbols) return undefined;
    return itemConfigs?.simulation?.[id]?.symbol || "circle";
  }
  function getSimulationId(id: number) {
    if (id == -1) return ctx.activeSimulationId ?? -1;
    return id;
  }
  function getItemTitle(
    item: SelectableChartItem<SelectableChartItemType> & { title?: string },
    shouldAbbreviate: boolean
  ): string {
    const uid = toUid(item.type, item.id);

    if (shouldAbbreviate && ctx.titleAbbreviations[uid] != null) {
      let title = ctx.titleAbbreviations[uid];
      return title;
    }

    let title = "";
    if (isItemType(item, "simulationRound")) {
      if (item.id == VIRTUAL_END_ROUND_INDEX) {
        return t("labels.endState");
      }
      title =
        formatRoundName(
          ctx.commonRoundSettings.durationType,
          ctx.commonRoundSettings.roundUnit,
          ctx.commonRoundSettings.roundLength,
          item.id,
          ctx.commonRoundSettings.timeStart
        ) || "";
    }
    if ("title" in item && item.title) {
      title = item.title;
    } else if (isItemType(item, "target")) {
      title = ctx.allAvailableTargets[item.id].title;
    } else if (isItemType(item, "resource")) {
      title = ctx.allAvailableResources[item.id].title;
    }

    if (shouldAbbreviate) {
      title = abbreviateTitle(title);
    }
    
    return title;
  }

  function getRoundColumnKey(simulationId: number, roundIndex: number) {
    if (chartType == "bar") return `simulation_${simulationId}.${roundIndex}`;
    return `round_${roundIndex}`;
  }

  function buildSimulationRoundColumns(
    columns: Map<string, ColumnEntry>,
    simulation: SimulationItem,
    rounds: SimulationRoundItem[]
  ) {
    // build columns
    for (const round of rounds) {
      const columnKey = getRoundColumnKey(simulation.id, round.index);
      let entry: ColumnEntry;

      const actionActivations = getRoundActivatedActions(
        simulation,
        round
      );
      const incidentActivations = getRoundActivatedIncidents(
        simulation,
        round
      );
      const effectActivations = getRoundActivatedScheduledEffects(
        simulation,
        round
      );

      const roundSettings = ctx.commonRoundSettings;

      if (!columns.has(columnKey)) {
        entry = {
          key: columnKey,
          id: round.index,
          type: "simulationRound",
          values: [],
          title:
            formatRoundName(
              roundSettings.durationType,
              roundSettings.roundUnit,
              roundSettings.roundLength,
              round.index,
              roundSettings.timeStart
            ) || "",
          shortTitle: `${round.index + 1}`
        };
        columns.set(columnKey, entry);
      }
      entry = columns.get(columnKey) as ColumnEntry;

      const subValues: EntryValue[] = [
        ...actionActivations,
        ...incidentActivations,
        ...effectActivations
      ].map(
        (activation): EntryValue => ({
          ...activation,
          shortTitle: activation.title,
          value: 0,
          startValue: 0,
          key: "",
          columnId: columnKey,
          unit: ""
        })
      );

      const simulationSymbol = getSimulationSymbol(simulation.id);

      const simulationId = getSimulationId(simulation.id);

      const values = entry.values || [];
      values.push({
        id: simulationId,
        uid: `round_${round.index}`,
        type: simulation.type,
        key: `simulation_${simulationId}.${round.index}`,
        value: 0,
        shortTitle: getItemTitle(simulation, true),
        title: getItemTitle(simulation, false),
        startValue: 0,
        columnId: columnKey,
        unit: "",
        simulationId: simulationId,
        roundIndex: round.index,
        symbol: simulationSymbol,
        subValues
      });
      entry.values = values;
    }
  }

  return {
    getColor,
    getItemColor,
    getRoundColor,
    getSimulationColor,
    getSimulationSymbol,
    getSimulationId,
    getItemTitle,
    getRoundColumnKey,
    buildSimulationRoundColumns
  }
}

function isItemType<T extends SelectableChartItemType>(
  item: SelectableChartItem | null | undefined,
  type: T
): item is SelectableChartItem<T> {
  return item?.type == type;
}

function getImpulseSubvalues<T extends SelectableChartItemType>(
  selectedElement: AvailableItem | null,
  simulationId: number,
  simulationRounds: SimulationRoundItem[],
  round: SimulationRoundItem & { previousRound?: SimulationRoundItem },
  startValue = 0
): EntryValue[] {
  if (!selectedElement) return [];

  const previousRound = round.previousRound;

  if (!isItemType(selectedElement, "target") && !isItemType(selectedElement, "resource")) return [];
  const elementType = selectedElement.type;
  const elementId = selectedElement.id;

  const noPrevious = previousRound == null || previousRound.index == round.index;
  
  
  const impulseRounds = simulationRounds.filter(
    (r) =>
      (noPrevious
        ? r.index == round.index
        : r.index >= previousRound.index) && r.index <= round.index
  );
  const sourceValues = impulseRounds.flatMap((round) => {
    const impulse = round.impulses?.[elementType]?.[elementId];
    const values = impulse?.sourceValues || [];

    return values.map((sourceValue) => {
      let value = sourceValue.value;
      if (sourceValue.valueToSet != null) {
        value = sourceValue.valueToSet - (impulse.valueBefore || 0);
      }
      return {
        key: `${simulationId}.${round.index}.${sourceValue.type}_${sourceValue.id}`,
        id: sourceValue.id,
        uid: `${sourceValue.type}_${sourceValue.id}`,
        columnId: `${simulationId}.${elementType}_${elementId}`,
        roundIndex: round.index,
        type: sourceValue.type,
        startValue: 0,
        value: value,
        title: sourceValue.type + sourceValue.id,
        shortTitle: sourceValue.type + sourceValue.id,
        numberFormat: "pointsFlexible" as const,
        simulationId: simulationId,
        unit: selectedElement.unit
      };
    });
  });

  // sort values in positive and negative stacks:
  const effectOrder = [
    "momentum",
    "relation",
    "delayed_incident",
    "incident",
    "delayed_action",
    "action",
    "scheduledEffect"
  ];

  let endY = startValue;
  const subValuesPositive = sourceValues
    .filter(hasPositiveValue)
    .sort(function (a, b) {
      const order = effectOrder.indexOf(a.type) - effectOrder.indexOf(b.type);

      if (order == 0) return b.value - a.value;
      return order;
    })
    // Build stacked Y coordinates
    .map((valueEntry): EntryValue => {
      const height = valueEntry.value;
      endY += height;
      return {
        ...valueEntry,
        type: valueEntry.type as ValueType,
        yStart: endY - height,
        yEnd: endY
      };
    });

  endY = startValue;
  const subValuesNegative = sourceValues
    .filter(hasNegativeValue)
    .sort(function (a, b) {
      const order = effectOrder.indexOf(a.type) - effectOrder.indexOf(b.type);

      if (order == 0) return a.value - b.value;
      return order;
    })
    // Build stacked Y coordinates
    .map((valueEntry): EntryValue => {
      const height = Math.abs(valueEntry.value);
      endY -= height;
      return {
        ...valueEntry,
        type: valueEntry.type as ValueType,
        yStart: endY + height,
        yEnd: endY
      };
    });

  return [...subValuesPositive, ...subValuesNegative];
}

function isVisibleColor(color: string | null | undefined) {
  if (!color) return true;
  return color.trim().toLowerCase() != "rgba(0,0,0,0)";
}

function hasPositiveValue<T extends { value?: number | null }>(i: T): i is (T & { value: number }) {
  if (i.value == null) return false;
  return i.value > 0;
}
function hasNegativeValue<T extends { value?: number | null }>(i: T): i is (T & { value: number }) {
  if (i.value == null) return false;
  return i.value < 0;
}
